import { RootState } from '@/redux/reducers'
import { sagaActions } from '@/redux/sagaActions'
import {
  FetchGameRoundPayload,
  PayloadFetchGame,
} from '@/redux/saga/fetchGameListSaga'
import { createSelector } from '@reduxjs/toolkit'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getGameTitle, getGameWithRoundTitle } from '@/utils/gameUtils'
import { resetGameAndRoundData } from '@/redux/reducers/gameReducer'

// Base selectors for extracting data
const selectGameState = (state: RootState) => state.games

// Combined selector that transforms the data
const gameDataSelector = createSelector([selectGameState], (gamesState) => ({
  // Transform the data here
  game: gamesState.game ?? null,
  round: gamesState.round ?? null,
  loading: {
    game: gamesState.loading?.game ?? false,
    round: gamesState.loading?.round ?? false,
  },
  error: gamesState.error ?? null,
}))

interface UseGameDataParams {
  gameId?: string
  roundId?: string
}

export const useGameData = ({ gameId, roundId }: UseGameDataParams) => {
  const dispatch = useDispatch()
  const { i18n } = useTranslation()

  const { game, round, loading, error } = useSelector(gameDataSelector)

  useEffect(() => {
    if (gameId) {
      const gamePayload: PayloadFetchGame = {
        gameId: parseInt(gameId),
      }
      dispatch({ type: sagaActions.FETCH_GAME, payload: gamePayload })
    }

    if (roundId) {
      const roundPayload: FetchGameRoundPayload = {
        roundId: parseInt(roundId),
      }
      dispatch({ type: sagaActions.FETCH_GAME_ROUND, payload: roundPayload })
      dispatch({
        type: sagaActions.FETCH_LIMIT_ROUND_NUMBERS,
        payload: roundPayload,
      })
    }
  }, [gameId, roundId, dispatch])

  useEffect(() => {
    return () => {
      // Reset game and round data when component unmounts
      dispatch(resetGameAndRoundData())
    }
  }, [dispatch])

  // Determine if we're loading based on what we're trying to fetch
  const isLoading = loading.game || loading.round

  return {
    game,
    round: roundId ? round : undefined, // Only return round if roundId was provided
    loading: isLoading,
    error,
    gameTitle: getGameTitle(i18n, game ?? undefined),
    roundGameTitle: getGameWithRoundTitle(
      i18n,
      game ?? undefined,
      round ?? undefined,
    ),
  }
}
